import { PRODUCTS_FORM_INFOS } from '@/consts/products';
import { logger } from '@/helpers/logger';
import useDataLayer from '@/hooks/useDataLayer/useDataLayer';
import type { UseFormState } from '@/hooks/useForm/interfaces';
import useForm from '@/hooks/useForm/useForm';
import TrustedShopsSimpleRating from '@/uikit/components/TrustedShops/TrustedShopsSimpleRating/TrustedShopsSimpleRating';
import { appendCampaignParams } from '@uikit/helpers/appendCampaignParams';
import Button from '@uikit/components/Button/Button';
import {
    ButtonAlign,
    ButtonColor,
    ButtonSize,
    ButtonType,
    ButtonWidth,
} from '@uikit/components/Button/consts';
import CheckboxWithLabel from '@uikit/components/CheckboxWithLabel/CheckboxWithLabel';
import { ClickableAction } from '@uikit/components/Clickable/consts';
import clsx from 'clsx';
import { FormFieldType } from '@uikit/components/FormField/consts';
import FormField from '@uikit/components/FormField/FormField';
import type { FormFieldElement } from '@uikit/components/FormField/interfaces';
import StageBoxFormBase from '@uikit/components/StageBoxFormBase/StageBoxFormBase';
import StageBoxSegmentedControl from '@uikit/components/StageBoxSegmentedControl/StageBoxSegmentedControl';
import { useEffect, useMemo, useState } from 'react';
import Stack from '../Stack/Stack';
import {
    PRODUCT_OPTIONS_ORDER,
    STAGE_BOX_SEGMENTED_CONTROL_CONFIG,
    stageBoxSegmentedControlColorMap,
} from './consts';
import schema from './schema';
import styles from './StageBoxCalculatorNew.module.scss';
import StageBoxConsumptionSelect from './StageBoxConsumptionSelect';
import type {
    CustomerTypeOption,
    ProductFormType,
    ProductOption,
    StageBoxCalculatorNewProps,
} from './types';

const sortProductOptions = (options: ProductOption[]) =>
  options.toSorted(
    (a, b) =>
      PRODUCT_OPTIONS_ORDER.indexOf(a) - PRODUCT_OPTIONS_ORDER.indexOf(b)
  );

const productOptionFromProductFormType = (productFormType: ProductFormType) =>
  (productFormType.split('-') as ProductOption[]) || [];


const StageBoxCalculatorNew = ({
  title,
  titleTag,
  productFormType,
  productCustomerType,
  powerGasCombinedDefault,
  backgroundType = 'gradient',
  powerPagePath,
  powerPageGwKPath,
  gasPagePath,
  gasPageGwKPath,
  powerAndGasPagePath,
  powerAndGasPageGwKPath,
  trustedShopsRating,
}: StageBoxCalculatorNewProps) => {
  const { pushDataLayerEvent } = useDataLayer();

  const isGradient = backgroundType === 'gradient';

  const productOptions = useMemo<ProductOption[]>(
    () => sortProductOptions(productOptionFromProductFormType(productFormType)),
    [productFormType]
  );

  const productPageLinkMap = useMemo(
    () => ({
      power: {
        private: powerPagePath,
        business: powerPageGwKPath,
      },
      gas: {
        private: gasPagePath,
        business: gasPageGwKPath,
      },
      combined: {
        private: powerAndGasPagePath,
        business: powerAndGasPageGwKPath,
      },
    }),
    [
      powerPagePath,
      powerPageGwKPath,
      gasPagePath,
      gasPageGwKPath,
      powerAndGasPagePath,
      powerAndGasPageGwKPath,
    ]
  );

  const isProductOption = (value: unknown): value is ProductOption => {
    return (
      typeof value === 'string' &&
      Object.keys(productPageLinkMap).includes(value)
    );
  };

  const isCustomerTypeOption = (
    value: unknown
  ): value is CustomerTypeOption => {
    return value === 'private' || value === 'business';
  };

  const createPageLink = (
    productOption: ProductOption | string | undefined,
    customerType: CustomerTypeOption | string | undefined
  ) => {
    if (!isProductOption(productOption) || !isCustomerTypeOption(customerType)) {
      return;
    }

    return productPageLinkMap[productOption][customerType];
  };

  const [productOption, setProductOption] = useState<ProductOption>(
    powerGasCombinedDefault ?? productOptions.find(Boolean) ?? 'power'
  );

  const customerType: CustomerTypeOption =
    productCustomerType === 'business-customer' ? 'business' : 'private';

  const [isExistingCustomer, setIsExistingCustomer] = useState(false);
  const [powerConsumption, setPowerConsumption] = useState(
    PRODUCTS_FORM_INFOS.power.options[1]?.value ?? ''
  );
  const [gasConsumption, setGasConsumption] = useState(
    PRODUCTS_FORM_INFOS.gas.options[1]?.value ?? ''
  );

  const handleOnSubmit = (formState: UseFormState): void => {
    if (!isProductOption(productOption) || !isCustomerTypeOption(customerType)) {
      logger.error('Invalid form data submitted:', {
        productOption,
        customerType,
      });
      return;
    }

    const pagePath = createPageLink(productOption, customerType);

    if (!pagePath) {
      logger.error('No calculator page path', {
        productOption,
        customerType,
      });
      return;
    }

    const zipcode = formState['zipcode'] ?? '';

    pushDataLayerEvent({
      event: 'customClick2',
      plz: zipcode,
      isLeadingTo: 'S&G-Konfigurator',
      isSgkModulePresent: 'true',
      sgkProductSelection: PRODUCTS_FORM_INFOS[productOption].radioLabel,
      sgkCustomerTypeSelection: customerType === 'private' ? 'Privat' : 'Gewerbe',
    });

    const searchParams = new URLSearchParams();
    searchParams.set('plz', zipcode);
    if (
      (productOption === 'power' || productOption === 'combined') &&
      powerConsumption
    ) {
      searchParams.set('s', powerConsumption);
    }
    if (
      (productOption === 'gas' || productOption === 'combined') &&
      gasConsumption
    ) {
      searchParams.set('g', gasConsumption);
    }
    searchParams.set('k', isExistingCustomer ? 'bk' : 'nk');

    window.location.href = appendCampaignParams(
      `${pagePath}?${searchParams.toString()}`,
      window.location.search
    );
  };

  const { handleSubmit, handleChange, handleBlur, getErrorByFieldName, formState } =
    useForm(handleOnSubmit, schema, { zipcode: '' });

  const hasProductOptions = productOptions.length > 1;
  const showPowerConsumption =
    productOption === 'power' || productOption === 'combined';
  const showGasConsumption =
    productOption === 'gas' || productOption === 'combined';

  const handleOnCustomerStatusChange = (
    $event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsExistingCustomer($event.target.checked);
  };

  const handleZipcodeChange = ($event: React.ChangeEvent<FormFieldElement>) => {
    $event.target.value = $event.target.value.replace(/\D/g, '');
    handleChange($event);
  };

  useEffect(() => {
    pushDataLayerEvent({
      event: 'sgkModuleUpdate',
      isSgkModulePresent: 'true',
      sgkProductSelection: PRODUCTS_FORM_INFOS[productOption].radioLabel,
      sgkCustomerTypeSelection: customerType === 'private' ? 'Privat' : 'Gewerbe',
    });
  }, [customerType, productOption, pushDataLayerEvent]);

  const buttonText = PRODUCTS_FORM_INFOS[productOption].buttonText;

  return (
    <form id="stagebox-form" onSubmit={handleSubmit} className={styles.form}>
      <StageBoxFormBase
        backgroundType={backgroundType}
        title={title}
        titleTag={titleTag}
        buttonSlot={   <Stack gap={1}>
            <Button
              type={ButtonType.Submit}
              actionType={ClickableAction.None}
              align={ButtonAlign.Center}
              color={isGradient ? ButtonColor.Secondary : ButtonColor.Primary}
              size={ButtonSize.Large}
              width={ButtonWidth.Full}
              noClickable
            >
              {isGradient ? (
                <span className={styles.submitLabel}>{buttonText}</span>
              ) : (
                buttonText
              )}
            </Button>

            {trustedShopsRating && (
              <TrustedShopsSimpleRating
                rating={trustedShopsRating}
                color={isGradient ? 'white' : 'default'}
              />
            )}
          </Stack>}
      >
        <div className={styles.formInner}>
          {hasProductOptions && (
            <StageBoxSegmentedControl
              className={isGradient ? styles.segmented : undefined}
              color={stageBoxSegmentedControlColorMap[backgroundType]}
              config={STAGE_BOX_SEGMENTED_CONTROL_CONFIG}
              onChange={setProductOption}
              selected={productOption}
            />
          )}

          <div className={styles.inputs}>
            <FormField
              name="zipcode"
              value={formState['zipcode'] || ''}
              label="PLZ"
              className={
                isGradient
                  ? clsx(styles.field, {
                      [styles.fieldError]: !!getErrorByFieldName('zipcode'),
                    })
                  : undefined
              }
              prefixBoxIcon="user/pin"
              type={FormFieldType.Text}
              autoComplete="postal-code"
              inputMode="numeric"
              maxLength={5}
              onChange={handleZipcodeChange}
              errorMessage={getErrorByFieldName('zipcode')}
              onBlur={handleBlur}
              restrictMaxLength
              isBoxOnGradient={isGradient}
              hasYellowError={isGradient}
              errorMessageHasRelativePosition
            />

            {showPowerConsumption && (
              <StageBoxConsumptionSelect
                className={isGradient ? styles.field : undefined}
                name="powerConsumption"
                label="Stromverbrauch"
                prefixBoxIcon="user/lightning"
                value={powerConsumption}
                options={PRODUCTS_FORM_INFOS.power.options}
                onChange={setPowerConsumption}
                isBoxOnGradient={isGradient}
              />
            )}

            {showGasConsumption && (
              <StageBoxConsumptionSelect
                className={isGradient ? styles.field : undefined}
                name="gasConsumption"
                label="Gasverbrauch"
                prefixBoxIcon="user/energy-fire"
                value={gasConsumption}
                options={PRODUCTS_FORM_INFOS.gas.options}
                onChange={setGasConsumption}
                isBoxOnGradient={isGradient}
              />
            )}
          </div>

          <div
            className={clsx(styles.customerStatus, {
              [styles.customerStatusOnGradient]: isGradient,
            })}
          >
            <CheckboxWithLabel
              id="customerStatus"
              name="customerStatus"
              label="Ich war oder bin enercity Kund:in"
              checked={isExistingCustomer}
              onChange={handleOnCustomerStatusChange}
              inverse={isGradient}
              className={isGradient ? styles.customerStatusBox : undefined}
            />
          </div>
        </div>
      </StageBoxFormBase>
    </form>
  );
};
export default StageBoxCalculatorNew;
